import {
  addMinutes,
  format,
  isBefore,
  isAfter,
  isWithinInterval,
  isToday,
  parseISO,
} from 'date-fns';
// global util
// types
import { ArrivalRisk, ArrivalRiskStatus, BookingSession } from '../types';
import { tableSortFilter } from '@/utils/arrayProcessing';
import { LabelColors, StatusWithSyleClass } from '@/types';
type FilterBookingsInputs = {
  tableData: BookingSession[];
  comparator: (a: any, b: any) => number;
  searchFieldValue: string;
  filterServiceId: number;
  filterServiceTypeId: number;
  filterStartDate: string | null;
  filterEndDate: string | null;
  filterStatusId: number;
  filterPaymentStatusId: number;
  //
  servicesWithTheirVariants: { id: number; name: string; variants: number[] }[];
};

export function applySortFilterSessions({
  tableData,
  comparator,
  searchFieldValue,
  filterServiceId,
  filterServiceTypeId,
  filterStartDate,
  filterEndDate,
  filterStatusId,
  filterPaymentStatusId,
  //
  servicesWithTheirVariants,
}: FilterBookingsInputs) {
  tableData = tableSortFilter(tableData, comparator);

  // ===================
  //     SEARCH BAR
  // ===================
  if (searchFieldValue) {
    const seachFieldValueLowerCase = searchFieldValue.toLowerCase();

    tableData = tableData.filter((session) => {
      const user = `${session.user?.first_name} ${session.user?.last_name}`;
      return (
        session.id.toString().indexOf(seachFieldValueLowerCase) !== -1 ||
        user.toLowerCase().indexOf(seachFieldValueLowerCase) !== -1 ||
        session.user.email.toLowerCase().indexOf(seachFieldValueLowerCase) !==
          -1 ||
        session.address.postcode
          .toLowerCase()
          .indexOf(seachFieldValueLowerCase) !== -1 ||
        session.address.postcode
          .toLowerCase()
          .replace(/\s/g, '')
          .indexOf(seachFieldValueLowerCase) !== -1 ||
        session.address.formatted_address
          .toLowerCase()
          .indexOf(seachFieldValueLowerCase) !== -1
        // booking.total_amount.toString().indexOf(seachFieldValueLowerCase) !== -1
      );
    });
  }

  // ===================
  //    SERVICE NAME
  // ===================
  if (filterServiceId !== 0) {
    tableData = tableData.filter((session) => {
      const selectedService = servicesWithTheirVariants.find(
        (service) => service.id === filterServiceId
      );
      return selectedService?.variants.includes(session.service_variant.id);
    });
  }

  // ==========================
  //    SERVICE TYPE/VARIANT
  // ==========================
  if (filterServiceTypeId !== 0) {
    tableData = tableData.filter(
      (session) => session.service_variant.id === filterServiceTypeId
    );
  }

  // ===================
  //        DATES
  // ===================
  if (filterStartDate && filterEndDate) {
    tableData = tableData.filter((session) => {
      if (session.date.length > 0) {
        return isWithinInterval(new Date(session.date), {
          start: new Date(filterStartDate),
          end: new Date(filterEndDate),
        });
      }
      return false;
    });
  }

  if (filterStartDate) {
    tableData = tableData.filter((session) => {
      if (session.date.length > 0) {
        return isAfter(new Date(session.date), new Date(filterStartDate));
      }
      return false;
    });
  }

  if (filterEndDate) {
    tableData = tableData.filter((session) => {
      if (session.date.length > 0) {
        return isBefore(new Date(session.date), new Date(filterEndDate));
      }
      return false;
    });
  }

  // ====================
  //    BOOKING STATUS
  // ====================
  if (filterStatusId !== 0) {
    tableData = tableData.filter(
      (session) => session.status_id === filterStatusId
    );
  }
  // ====================
  //    PAYMENT STATUS
  // ====================
  if (filterPaymentStatusId !== 0) {
    tableData = tableData.filter(
      (session) => session.payment_status_id === filterPaymentStatusId
    );
  }

  return tableData;
}

export const getNamedInputValueFromForm = (
  event: React.FormEvent<HTMLFormElement>,
  inputName: string
) => {
  const form = event.target as HTMLFormElement;
  const input = form.elements.namedItem(inputName) as HTMLInputElement;
  return input.value;
};

// =================
//    TABLE HEAD
// =================

export const SESSIONS_TABLE_HEAD = [
  { id: 'id', label: 'Session ID', align: 'left' },
  {
    id: 'service_variant.name',
    label: 'Service type',
    align: 'left',
  },
  { id: 'date', label: 'Date', align: 'left' },
  { id: 'time', label: 'Time', align: 'left' },
  { id: 'total_hours', label: 'Total hours', align: 'left', width: '20px' },
  { id: 'user.first_name', label: 'Customer Details', align: 'left' },
  {
    id: 'booking_session_cleaners.first_name',
    label: 'Cleaner',
    align: 'left',
  },
  { id: 'address.formatted_address', label: 'Address', align: 'left' },
  {
    id: 'booking_session_status.display',
    label: 'Session status',
    align: 'left',
  },
  { id: 'toggle-accordion' },
];

export const returnSessionFilterFromDate = (dateString: string) => {
  const date = parseISO(dateString);
  const today = new Date();

  if (isToday(date)) {
    return 'today';
  } else if (isBefore(date, today)) {
    return 'previous';
  } else if (isAfter(date, today)) {
    return 'upcoming';
  }

  return 'error'; // In case the date is invalid or some other issue
};

const getSessionStatusFilterValue = (session: BookingSession) =>
  (
    session.booking_session_status?.value ??
    session.booking_session_status?.display ??
    ''
  ).toLowerCase();

const isCancelledSession = (session: BookingSession) => {
  const sessionStatus = getSessionStatusFilterValue(session);
  return sessionStatus === 'cancelled' || sessionStatus === 'canceled';
};

export const getDeepLinkedSessionFilter = (session: BookingSession) => {
  if (isCancelledSession(session)) {
    return 'cancelled';
  }

  if ((session.booking_session_cleaners ?? []).length === 0) {
    return 'unassigned';
  }

  const filterFromDate = returnSessionFilterFromDate(session.date);
  return filterFromDate === 'error' ? 'today' : filterFromDate;
};

export const sessionMatchesSessionListFilter = (
  session: BookingSession,
  sessionFilter: string
) => {
  const normalizedFilter = sessionFilter.toLowerCase();

  switch (normalizedFilter) {
    case 'previous':
    case 'today':
    case 'upcoming':
      return returnSessionFilterFromDate(session.date) === normalizedFilter;
    case 'unassigned':
      return (session.booking_session_cleaners ?? []).length === 0;
    case 'cancelled':
      return isCancelledSession(session);
    default:
      return false;
  }
};

export const shouldUseCompletedLastOrdering = (sessionFilter: string) =>
  sessionFilter.toLowerCase() === 'today';

export const shouldUseTodayBottomStatusOrdering =
  shouldUseCompletedLastOrdering;

export const DEFAULT_SESSION_LIST_SORT = {
  order: 'desc',
  orderBy: 'id',
} as const;

export const TODAY_SESSION_LIST_SORT = {
  order: 'asc',
  orderBy: 'time',
} as const;

export const getDefaultSessionListSort = (sessionFilter: string) =>
  shouldUseCompletedLastOrdering(sessionFilter)
    ? TODAY_SESSION_LIST_SORT
    : DEFAULT_SESSION_LIST_SORT;

export const convertStatusHistoryCreatedAt = (
  createdAtDateTimeString: string
) => {
  // the app uses a format like this and that's what we get in the API response
  // created_at: "09-09-2024 09:43"
  const [datePart, timePart] = createdAtDateTimeString.split(' ');

  // Split the date part into day, month, and year
  const [day, month, year] = datePart.split('-').map(Number);

  // Split the time part into hours and minutes
  const [hours, minutes] = timePart.split(':').map(Number);

  // Create a new Date object (month is zero-based in JS Date object)
  const date = new Date(year, month - 1, day, hours, minutes);

  return date;
};

export const returnStatusObject = (
  statusName: string,
  statuses: StatusWithSyleClass[]
) => {
  const notFound = {
    id: 0,
    value: 'NOT_FOUND',
    display: '??Error??',
    style_class: 'error',
  };
  const statusObject = statuses.find(
    (s) => s.display.toLowerCase() === statusName.toLowerCase()
  );

  return statusObject || notFound;
};

export const getInputValue = (
  event: React.FormEvent<HTMLFormElement>,
  inputName: string
) => {
  const form = event.target as HTMLFormElement;
  const input = form.elements.namedItem(inputName) as HTMLInputElement;
  return input.value;
};

const parseTimeOnDate = (dateString: string, timeString?: string | null) => {
  if (timeString === undefined || timeString === null) return null;
  const trimmedTime =
    typeof timeString === 'string'
      ? timeString.trim()
      : String(timeString).trim();
  if (!trimmedTime) return null;

  const treatAsIso = trimmedTime.includes('T');
  const valueToParse = treatAsIso
    ? trimmedTime
    : `${dateString}T${
        trimmedTime.length === 5 ? `${trimmedTime}:00` : trimmedTime
      }`;

  const parsed = parseISO(valueToParse);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const parseTimeslotBoundary = (
  dateString: string,
  timeslot?: string,
  index?: number
) => {
  if (!timeslot?.includes('-')) return null;
  const segments = timeslot.split('-');
  const segmentValue = segments[index ?? 0]?.trim();
  return parseTimeOnDate(dateString, segmentValue);
};

const hasDateComponent = (value: string) =>
  /\d{4}-\d{1,2}-\d{1,2}/.test(value) || /\d{1,2}-\d{1,2}-\d{4}/.test(value);

const parseDateTimeString = (value?: string | null) => {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;

  const containsDate = hasDateComponent(trimmed) || trimmed.includes('T');
  if (containsDate || trimmed.includes(' ')) {
    const isoCandidate =
      trimmed.includes('T') || !trimmed.includes(' ')
        ? trimmed
        : trimmed.replace(' ', 'T');
    const parsed = parseISO(isoCandidate);
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }

  if (/^\d{2}-\d{2}-\d{4}/.test(trimmed)) {
    const fallback = convertStatusHistoryCreatedAt(trimmed);
    if (!Number.isNaN(fallback.getTime())) return fallback;
  }

  return null;
};

type StatusHistoryEntry = {
  status?: string | null;
  created_at?: string | null;
  arrival_risk?: ArrivalRisk | null;
};

type ArrivalRiskPresentation = {
  color: LabelColors;
  detail: string | null;
  label: string;
  text: string;
  variant: 'filled' | 'outlined' | 'ghost';
};

const ACTUAL_START_STATUS_VALUES = ['IN_PROGRESS', 'STARTED'];
export const COMPLETED_SESSION_STATUS_VALUES = [
  'COMPLETED',
  'DONE',
  'FINISHED',
];
export const VOID_SESSION_STATUS_VALUES = ['VOID'];
export const TODAY_SESSION_AFTER_COMPLETED_STATUS_VALUES = [
  'PAUSED',
  'CANCELLED',
  'CANCELED',
  'VOID',
];
export const TODAY_SESSION_BOTTOM_STATUS_VALUES = [
  ...COMPLETED_SESSION_STATUS_VALUES,
  ...TODAY_SESSION_AFTER_COMPLETED_STATUS_VALUES,
];
export const SESSION_SUMMARY_EXCLUDED_STATUS_VALUES = [
  'PAUSED',
  'CANCELLED',
  'CANCELED',
  'VOID',
];
export const SESSION_SUMMARY_EXCLUDED_PAYMENT_STATUS_VALUES = ['EOM'];

const normaliseStatusKey = (value?: string | null) => {
  if (!value) return null;
  return value
    .trim()
    .replace(/[\s-]+/g, '_')
    .toUpperCase();
};

export const isCompletedBookingSessionStatus = (
  status?: BookingSession['booking_session_status'] | null
) => {
  const statusKey =
    normaliseStatusKey(status?.value) ?? normaliseStatusKey(status?.display);
  return Boolean(
    statusKey && COMPLETED_SESSION_STATUS_VALUES.includes(statusKey)
  );
};

export const isVoidBookingSessionStatus = (
  status?: BookingSession['booking_session_status'] | null
) => {
  const statusKey =
    normaliseStatusKey(status?.value) ?? normaliseStatusKey(status?.display);
  return Boolean(statusKey && VOID_SESSION_STATUS_VALUES.includes(statusKey));
};

export const isTodaySessionBottomStatus = (
  status?: BookingSession['booking_session_status'] | null
) => {
  const statusKey =
    normaliseStatusKey(status?.value) ?? normaliseStatusKey(status?.display);
  return Boolean(
    statusKey && TODAY_SESSION_BOTTOM_STATUS_VALUES.includes(statusKey)
  );
};

export const isTodaySessionAfterCompletedStatus = (
  status?: BookingSession['booking_session_status'] | null
) => {
  const statusKey =
    normaliseStatusKey(status?.value) ?? normaliseStatusKey(status?.display);
  return Boolean(
    statusKey && TODAY_SESSION_AFTER_COMPLETED_STATUS_VALUES.includes(statusKey)
  );
};

export const sortTodaySessionsWithBottomStatuses = <
  T extends Pick<BookingSession, 'booking_session_status'>,
>(
  sessions: T[]
) => {
  const prioritySessions: T[] = [];
  const completedSessions: T[] = [];
  const afterCompletedSessions: T[] = [];

  sessions.forEach((session) => {
    if (isTodaySessionAfterCompletedStatus(session.booking_session_status)) {
      afterCompletedSessions.push(session);
      return;
    }

    if (isCompletedBookingSessionStatus(session.booking_session_status)) {
      completedSessions.push(session);
      return;
    }

    prioritySessions.push(session);
  });

  return [...prioritySessions, ...completedSessions, ...afterCompletedSessions];
};

export const isSessionSummaryExcludedStatus = (
  status?: BookingSession['booking_session_status'] | null
) => {
  const statusKey =
    normaliseStatusKey(status?.value) ?? normaliseStatusKey(status?.display);
  return Boolean(
    statusKey && SESSION_SUMMARY_EXCLUDED_STATUS_VALUES.includes(statusKey)
  );
};

export const isSessionSummaryExcludedPaymentStatus = (
  status?: BookingSession['booking_session_payment_status'] | null
) => {
  const statusKey =
    normaliseStatusKey(status?.value) ?? normaliseStatusKey(status?.display);
  return Boolean(
    statusKey &&
      SESSION_SUMMARY_EXCLUDED_PAYMENT_STATUS_VALUES.includes(statusKey)
  );
};

export const shouldIncludeBookingSessionInSummary = (
  session: Pick<
    BookingSession,
    'booking_session_status' | 'booking_session_payment_status'
  >
) =>
  !isSessionSummaryExcludedStatus(session.booking_session_status) &&
  !isSessionSummaryExcludedPaymentStatus(
    session.booking_session_payment_status
  );

const ARRIVAL_RISK_PRESENTATION: Record<
  ArrivalRiskStatus,
  Pick<ArrivalRiskPresentation, 'color' | 'label' | 'variant'>
> = {
  on_time: {
    color: 'success',
    label: 'On time',
    variant: 'ghost',
  },
  at_risk: {
    color: 'warning',
    label: 'At risk',
    variant: 'ghost',
  },
  late: {
    color: 'error',
    label: 'Late',
    variant: 'ghost',
  },
  unknown: {
    color: 'default',
    label: 'Unknown',
    variant: 'ghost',
  },
};

const isOnTheWayHistoryEntry = (entry?: StatusHistoryEntry | null) => {
  const statusKey = normaliseStatusKey(entry?.status);
  return Boolean(statusKey?.includes('ON_THE_WAY'));
};

const getArrivalRiskSortTime = (entry: StatusHistoryEntry) => {
  const checkedAt = parseDateTimeString(entry.arrival_risk?.checked_at ?? null);
  const createdAt = parseDateTimeString(entry.created_at ?? null);
  return checkedAt?.getTime() ?? createdAt?.getTime() ?? 0;
};

export const getLatestArrivalRiskEntry = (
  statusHistory?: StatusHistoryEntry[] | null,
  onlyOnTheWay = false
) => {
  if (!statusHistory?.length) return null;

  return statusHistory.reduce<StatusHistoryEntry | null>((latest, entry) => {
    if (!entry.arrival_risk) return latest;
    if (onlyOnTheWay && !isOnTheWayHistoryEntry(entry)) return latest;
    if (!latest) return entry;

    return getArrivalRiskSortTime(entry) >= getArrivalRiskSortTime(latest)
      ? entry
      : latest;
  }, null);
};

export const getLatestOnTheWayArrivalRisk = (
  session?: Pick<BookingSession, 'status_history'> | null
) =>
  getLatestArrivalRiskEntry(session?.status_history, true)?.arrival_risk ??
  null;

export const formatArrivalRiskEta = (value?: string | null) => {
  const date = parseDateTimeString(value ?? null);
  if (!date) return null;
  return format(date, 'HH:mm');
};

export const formatArrivalRiskMinutesLate = (minutes?: number | null) => {
  if (typeof minutes !== 'number' || Number.isNaN(minutes) || minutes <= 0) {
    return null;
  }
  return `${Math.round(minutes)}m late`;
};

export const getArrivalRiskPresentation = (
  risk?: ArrivalRisk | null
): ArrivalRiskPresentation | null => {
  if (!risk?.status) return null;

  const presentation =
    ARRIVAL_RISK_PRESENTATION[risk.status] ?? ARRIVAL_RISK_PRESENTATION.unknown;

  let detail: string | null = null;
  if (risk.status === 'late') {
    detail = formatArrivalRiskMinutesLate(risk.minutes_late);
  } else if (risk.status === 'at_risk' || risk.status === 'on_time') {
    const eta = formatArrivalRiskEta(risk.estimated_arrival_at);
    detail = eta ? `ETA ${eta}` : null;
  } else if (risk.status === 'unknown' && risk.reason) {
    detail = risk.reason;
  }

  return {
    ...presentation,
    detail,
    text: detail ? `${presentation.label} · ${detail}` : presentation.label,
  };
};

const getActualStartFromHistory = (history?: StatusHistoryEntry[] | null) => {
  if (!history?.length) return null;
  let earliest: Date | null = null;

  history.forEach((entry) => {
    const statusKey = normaliseStatusKey(entry?.status);
    if (!statusKey || !ACTUAL_START_STATUS_VALUES.includes(statusKey)) return;

    const timestamp = parseDateTimeString(entry?.created_at ?? null);
    if (!timestamp) return;

    if (!earliest || timestamp < earliest) {
      earliest = timestamp;
    }
  });

  return earliest;
};

export const getActualSessionStart = (
  session?: Partial<BookingSession> | null
) => {
  if (!session) return null;
  return (
    getActualStartFromHistory(session.booking_cleaner_session_status_history) ||
    getActualStartFromHistory(session.status_history)
  );
};

const toFiniteSessionNumber = (value: unknown) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string' && value.trim() === '') return null;

  const numericValue =
    typeof value === 'number' ? value : Number(String(value).trim());

  return Number.isFinite(numericValue) ? numericValue : null;
};

const firstFiniteSessionNumber = (...values: unknown[]) => {
  for (const value of values) {
    const numericValue = toFiniteSessionNumber(value);
    if (numericValue !== null) return numericValue;
  }

  return null;
};

const getBookingItemsHours = (session?: Partial<BookingSession> | null) => {
  const bookingItems = session?.booking_items;
  if (!Array.isArray(bookingItems)) return null;

  const totalHours = bookingItems.reduce((total, item) => {
    const itemHours = toFiniteSessionNumber(item.qty);
    return total + (itemHours ?? 0);
  }, 0);

  return totalHours > 0 ? totalHours : null;
};

export const resolveBookingSessionBillableHours = (
  session?: Partial<BookingSession> | null
) => {
  if (!session) return 0;
  const bookingItemsHours = getBookingItemsHours(session);

  const billingTotalHours = firstFiniteSessionNumber(
    session.billing?.billing_total_hours,
    session.billing_total_hours
  );
  if (billingTotalHours !== null) return billingTotalHours;

  const hasBillingBreakdown =
    firstFiniteSessionNumber(
      session.billing?.cleaning_hours,
      session.billing?.extras_hours,
      session.billing?.extra_hours,
      session.cleaning_hours,
      session.extras_hours
    ) !== null || bookingItemsHours !== null;

  if (hasBillingBreakdown) {
    const cleaningHours =
      firstFiniteSessionNumber(
        session.billing?.cleaning_hours,
        session.cleaning_hours,
        session.hours
      ) ?? 0;
    const cleaningExtrasHours =
      firstFiniteSessionNumber(
        session.billing?.extras_hours,
        session.extras_hours,
        bookingItemsHours
      ) ?? 0;
    const extraHours =
      firstFiniteSessionNumber(
        session.billing?.extra_hours,
        session.extra_hours
      ) ?? 0;

    return cleaningHours + cleaningExtrasHours + extraHours;
  }

  const legacyTotalHours = firstFiniteSessionNumber(session.total_hours);
  if (legacyTotalHours !== null) return legacyTotalHours;

  const fallbackBaseHours = firstFiniteSessionNumber(session.hours);
  const fallbackExtraHours = firstFiniteSessionNumber(session.extra_hours);

  if (fallbackBaseHours !== null || fallbackExtraHours !== null) {
    return (fallbackBaseHours ?? 0) + (fallbackExtraHours ?? 0);
  }

  return 0;
};

export const formatHoursAndMinutes = (minutes: number) => {
  if (minutes <= 0) return '0m';
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (hours && remainingMinutes) {
    return `${hours}h ${remainingMinutes}m`;
  }
  if (hours) {
    return `${hours}h`;
  }
  return `${remainingMinutes}m`;
};

export const formatDurationFromHours = (hoursValue: number | string) => {
  const numericHours = Number(hoursValue);
  if (Number.isNaN(numericHours) || numericHours <= 0) {
    return '0m';
  }

  const totalMinutes = Math.round(numericHours * 60);
  return formatHoursAndMinutes(totalMinutes);
};

export const resolveSessionRange = (
  session?: Partial<BookingSession> | null
) => {
  if (!session?.date) return null;

  const start =
    parseDateTimeString(
      typeof session.start_time === 'string' ? session.start_time : null
    ) ||
    parseTimeOnDate(session.date, session.time) ||
    parseTimeslotBoundary(session.date, session.timeslot, 0);

  if (!start || Number.isNaN(start.getTime())) return null;

  const scheduledDurationMinutes = Math.max(
    resolveBookingSessionBillableHours(session) * 60,
    0
  );

  let end: Date | null =
    scheduledDurationMinutes > 0
      ? addMinutes(start, scheduledDurationMinutes)
      : null;

  if (!end || Number.isNaN(end.getTime())) {
    end =
      parseDateTimeString(
        typeof session.end_time === 'string' ? session.end_time : null
      ) ||
      parseTimeOnDate(session.date, session.end_time) ||
      parseTimeslotBoundary(session.date, session.timeslot, 1) ||
      null;
  }

  if (!end || Number.isNaN(end.getTime())) return null;

  return { start, end };
};
